import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaCamera, FaFilm, FaHeart, FaStar } from 'react-icons/fa';
import SEO from '../components/SEO';
import SectionWrapper from '../components/SectionWrapper';
import AnimatedItem from '../components/AnimatedItem';
import HeroVideo from '../components/HeroVideo';
import BookedDatesTicker from '../components/BookedDatesTicker';
import MarqueeRows from '../components/MarqueeRows';
import Partners from '../components/Partners';
import { getHomePhotoRows, getHomeFilmRows } from '../data/homePortfolio';
import {
  BRAND_NAME,
  BRAND_TAGLINE,
  BRAND_DEFAULT_TITLE,
  BRAND_DEFAULT_DESCRIPTION,
  brandOrganizationDescription,
} from '../constants/brand';
import '../styles/Home.css';

const Home = () => {
  const photoRows = useMemo(() => getHomePhotoRows(5), []);
  const filmRows = useMemo(() => getHomeFilmRows(5), []);

  useEffect(() => {
    // Instant jump so scroll-scrub hero starts at frame 0 (smooth scroll fights pin init)
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <>
      <SEO 
        title={BRAND_DEFAULT_TITLE}
        description={BRAND_DEFAULT_DESCRIPTION}
        keywords="Stories Beyond Weddings, storytelling wedding photography Bangladesh, cinematic wedding photography CHT, indigenous wedding photography, Indigenous group wedding photography CHT, Chakma wedding photography, Marma wedding photography, Tripura wedding photography, Tanchangya wedding photography, CHT wedding photographer, Bandarban wedding photography, Rangamati wedding cinematography, Khagrachari wedding photographer, Cox's Bazar wedding photography, Chittagong wedding cinematography, documentary wedding photography, narrative wedding photography, artistic wedding storytelling, emotional wedding photography, candid wedding photography, natural wedding photography, authentic wedding moments, wedding day storytelling, love story photography, wedding memories, timeless wedding photography, artistic wedding portraits, creative wedding photography"
        image="https://khuyungwedstories.com/albums/logo/logo.png"
        url="https://khuyungwedstories.com/"
        canonical="https://khuyungwedstories.com/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": `${BRAND_NAME} - ${BRAND_TAGLINE}`,
          "description": BRAND_DEFAULT_DESCRIPTION,
          "url": "https://khuyungwedstories.com/",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": BRAND_NAME,
            "image": "https://khuyungwedstories.com/albums/logo/logo.png",
            "description": brandOrganizationDescription(),
            "slogan": BRAND_TAGLINE,
            "telephone": "+8801601719735",
            "email": "khuyungwedstories@gmail.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Bandarban Sadar",
              "addressLocality": "Bandarban",
              "addressRegion": "Chittagong Hill Tracts",
              "postalCode": "4600",
              "addressCountry": "BD"
            },
            "priceRange": "BDT 15,000 - BDT 100,000",
            "currenciesAccepted": "BDT",
            "paymentAccepted": "Cash, Bank Transfer, Mobile Banking",
            "openingHours": "Mo-Su 09:00-18:00",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Storytelling Wedding Photography Packages",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Storytelling Wedding Photography",
                    "description": "Professional storytelling wedding photography capturing authentic emotions and cultural traditions"
                  }
                }
              ]
            }
          }
        }}
        serviceData={[
          {
            name: "Storytelling Wedding Photography",
            description: `${BRAND_TAGLINE} Professional storytelling wedding photography capturing authentic love stories and cultural traditions in CHT`
          },
          {
            name: "Wedding Cinematography",
            description: `${BRAND_TAGLINE} Cinematic wedding films and storytelling videography preserving cultural heritage and wedding traditions`
          },
          {
            name: "Indigenous Wedding Photography",
            description: `${BRAND_TAGLINE} Specialized photography of Chakma, Marma, Tripura, and Tanchangya wedding ceremonies and traditions`
          }
        ]}
      />

      <main id="main-content" role="main" aria-label="Main content">
        {/* 1. Hero */}
        <HeroVideo />

        {/* 2. Booked dates */}
        <BookedDatesTicker />

        {/* 3. About */}
        <SectionWrapper delay={0.1} direction="right">
          <section className="home-about">
            <div className="home-about-inner">
              <div className="home-about-image-wrap">
                <AnimatedItem delay={0.2} direction="left">
                  <div className="home-about-image" style={{ backgroundImage: 'url(/albums/hero/aboutHomepageImage.webp)' }} />
                </AnimatedItem>
              </div>
              <div className="home-about-text">
                <AnimatedItem delay={0.2} direction="up">
                  <p className="home-eyebrow">The Studio</p>
                  <h2 className="home-section-title">About Khuyung <span className="dancing-script">WedStories</span></h2>
                </AnimatedItem>
                <AnimatedItem delay={0.3} direction="up">
                  <p className="home-about-lead">
                    We don't just take pictures - we craft visual poetry that tells your unique love story.
                  </p>
                </AnimatedItem>
                <AnimatedItem delay={0.4} direction="up">
                  <p className="home-about-body">
                    With 5+ years of experience, our team specializes in storytelling wedding photography and cinematography across CHT, Cox's Bazar, and nationwide. From the misty hills of <Link to="/bandarban-wedding-photography">Bandarban</Link> to the serene lakes of <Link to="/rangamati-wedding-photography">Rangamati</Link>, <Link to="/khagrachhari-wedding-photography">Khagrachhari</Link>, and <Link to="/cox-bazar-wedding-photography">Cox's Bazar</Link> - we immortalize every smile, tear, and embrace.
                  </p>
                </AnimatedItem>
                <AnimatedItem delay={0.5} direction="up">
                  <Link to="/about" className="home-about-cta">Learn more about us</Link>
                </AnimatedItem>
              </div>
            </div>
          </section>
        </SectionWrapper>

        {/* 4. Photos marquee */}
        <section className="home-portfolio-marquee" aria-labelledby="home-photos-heading">
          <div className="home-portfolio-marquee-inner">
            <AnimatedItem delay={0.1} direction="up">
              <div className="home-section-head">
                <div>
                  <p className="home-eyebrow">Portfolio</p>
                  <h2 id="home-photos-heading" className="home-section-title">
                    Our <span className="dancing-script">Stories</span>
                  </h2>
                </div>
                <p className="home-section-subtitle">
                  Wedding photography and cinematic films, told frame by frame
                </p>
              </div>
            </AnimatedItem>
          </div>
          <MarqueeRows rows={photoRows} variant="photo" linkTo="/photos" />
          <div className="home-portfolio-marquee-inner home-portfolio-marquee-cta-wrap">
            <Link to="/photos" className="home-portfolio-cta">
              View full gallery →
            </Link>
          </div>
        </section>

        {/* 5. Films marquee */}
        <section className="home-films-marquee" aria-labelledby="home-films-heading">
          <div className="home-portfolio-marquee-inner">
            <AnimatedItem delay={0.1} direction="up">
              <div className="home-section-head">
                <div>
                  <p className="home-eyebrow">Films</p>
                  <h2 id="home-films-heading" className="home-section-title">
                    Wedding <span className="dancing-script">Films</span>
                  </h2>
                </div>
                <p className="home-section-subtitle">
                  Cinematic love stories in motion
                </p>
              </div>
            </AnimatedItem>
          </div>
          <MarqueeRows rows={filmRows} variant="video" linkTo="/films" />
          <div className="home-portfolio-marquee-inner home-portfolio-marquee-cta-wrap">
            <Link to="/films" className="home-portfolio-cta">
              Watch all films →
            </Link>
          </div>
        </section>

        {/* 6. Why Choose Us */}
        <section className="home-why-choose">
          <div className="home-why-inner">
            <AnimatedItem delay={0.1} direction="up">
              <div className="home-section-head">
                <div>
                  <p className="home-eyebrow">Why Us</p>
                  <h2 className="home-section-title">Why Choose <span className="dancing-script">Us</span></h2>
                </div>
                <p className="home-section-subtitle">What sets our storytelling wedding photography apart</p>
              </div>
            </AnimatedItem>
            <div className="home-why-grid">
              <AnimatedItem delay={0.2} direction="up">
                <div className="home-why-card">
                  <div className="home-why-heading">
                    <div className="home-why-icon"><FaCamera /></div>
                    <h3>CHT Specialists</h3>
                  </div>
                  <p>Top wedding photography team in Chittagong Hill Tracts with 5+ years and 300+ weddings.</p>
                </div>
              </AnimatedItem>
              <AnimatedItem delay={0.3} direction="up">
                <div className="home-why-card">
                  <div className="home-why-heading">
                    <div className="home-why-icon"><FaFilm /></div>
                    <h3>Cinematic Storytelling</h3>
                  </div>
                  <p>Indigenous wedding traditions and cultural moments captured with artistic vision.</p>
                </div>
              </AnimatedItem>
              <AnimatedItem delay={0.4} direction="up">
                <div className="home-why-card">
                  <div className="home-why-heading">
                    <div className="home-why-icon"><FaHeart /></div>
                    <h3>Family-like Team</h3>
                  </div>
                  <p>Creative, passionate, and detail-oriented - we make your journey stress-free.</p>
                </div>
              </AnimatedItem>
              <AnimatedItem delay={0.5} direction="up">
                <div className="home-why-card">
                  <div className="home-why-heading">
                    <div className="home-why-icon"><FaStar /></div>
                    <h3>Flexible Packages</h3>
                  </div>
                  <p>Affordable, customizable options and delivery within committed time.</p>
                </div>
              </AnimatedItem>
            </div>
          </div>
        </section>

        {/* 7. How We Work */}
        <section className="home-how">
          <div className="home-how-inner">
            <AnimatedItem delay={0.1} direction="up">
              <div className="home-section-head">
                <div>
                  <p className="home-eyebrow">The Process</p>
                  <h2 className="home-section-title">How We <span className="dancing-script">Work</span></h2>
                </div>
                <p className="home-section-subtitle">From booking to delivery - simple and transparent</p>
              </div>
            </AnimatedItem>
            <div className="home-steps">
              {[
                { num: '1', title: 'Book Your Date', desc: 'Secure your date with a confirmation.' },
                { num: '2', title: 'Share Your Vision', desc: 'We discuss your preferences and style.' },
                { num: '3', title: 'Storytelling Plan', desc: 'We prepare the shoot and storytelling layout.' },
                { num: '4', title: 'Your Big Day', desc: 'We capture every detail on the day.' },
                { num: '5', title: 'Edit', desc: 'We edit your photos and films with care and artistry.' },
                { num: '6', title: 'Deliver', desc: 'Photos and films delivered on time, the way you prefer.' },
              ].map((step, i) => (
                <AnimatedItem
                  key={step.num}
                  delay={0.15 + i * 0.08}
                  direction="up"
                  className="home-step-cell"
                >
                  <div className="home-step-card" data-step={step.num}>
                    <div className="home-step-heading">
                      <span className="home-step-num">{step.num}</span>
                      <h3>{step.title}</h3>
                    </div>
                    <p>{step.desc}</p>
                  </div>
                </AnimatedItem>
              ))}
            </div>
          </div>
        </section>

        {/* 8. Partners */}
        <SectionWrapper delay={0.1} direction="left">
          <Partners />
        </SectionWrapper>

        {/* 9. Budget CTA */}
        <section className="home-budget-cta">
          <div className="home-budget-inner">
            <AnimatedItem delay={0.1} direction="up">
              <p className="home-budget-label">Budget tool</p>
              <h2 className="home-section-title home-budget-title">Plan Your <span className="dancing-script">Budget</span></h2>
              <p className="home-budget-subtitle">
                Get an instant estimate based on duration, team size, and location - then explore packages that fit.
              </p>
            </AnimatedItem>
            <div className="home-budget-features">
              <span className="home-budget-feature">Instant estimates</span>
              <span className="home-budget-feature">Accurate pricing</span>
              <span className="home-budget-feature">Custom packages</span>
            </div>
            <AnimatedItem delay={0.2} direction="up">
              <div className="home-budget-actions">
                <Link to="/budget-calculator" className="home-budget-btn home-budget-btn-primary">Calculate Budget</Link>
                <Link to="/packages" className="home-budget-btn home-budget-btn-outline">View Packages</Link>
              </div>
            </AnimatedItem>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
